import DepartmentSelect from "@/components/DepartmentSelect";
import { useCodeValues, useDepartmentSearchIds } from "@/services/commonService";
import { SearchQuery } from "@/types/common";
import { DepartmentRoleType, DepartmentTreeNode } from "@/types/department";
import { InstructionSearchCondition } from "@/types/instruction";
import { updateObjectState } from "@/utils/updateState";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

const ALL_SELECTED_TEXT = "전체";
interface SearchAreaProps {
  search: SearchQuery<InstructionSearchCondition>;
  onSearch: (searchQuery: SearchQuery<InstructionSearchCondition>) => void;
  role: "approver" | "manager" | "admin";
}

const SearchArea: React.FC<SearchAreaProps> = ({ search, onSearch }) => {
  const { data: codeValues } = useCodeValues();
  const [selectedOwnerDept, setSelectedOwnerDept] = useState<DepartmentTreeNode[]>([]);
  const [selectedCoopDepts, setSelectedCoopDepts] = useState<DepartmentTreeNode[]>([]);
  const [localConditions, setLocalConditions] = useState<InstructionSearchCondition>({
    ...search.conditions,
  });

  const ownerDeptId = Number(search.conditions?.ownerDepartment);
  const findIds = search.conditions?.ownerDepartment ? [search.conditions?.ownerDepartment] : [];
  if (search.conditions?.ccDepartments) {
    findIds.push(...search.conditions.ccDepartments);
  }

  if (!isNaN(ownerDeptId)) {
    findIds.push(ownerDeptId);
  }

  const { data: searchDepts, isLoading: isLoadingDepts } = useDepartmentSearchIds(findIds);

  useEffect(() => {
    setLocalConditions({
      ...search.conditions,
    });
  }, [search.conditions]);

  useEffect(() => {
    if (searchDepts && !isLoadingDepts) {
      setSelectedOwnerDept(searchDepts.filter((dept) => dept.id === search.conditions?.ownerDepartment));
      setSelectedCoopDepts(searchDepts.filter((dept) => search.conditions?.ccDepartments?.includes(dept.id)));
    }
  }, [searchDepts, isLoadingDepts, search.conditions]);

  const allStatusIds = useMemo(
    () => codeValues?.INSTRUCTION_STATUS?.map((status) => status.id) || [],
    [codeValues?.INSTRUCTION_STATUS]
  );

  const isAllSelected = useMemo(() => {
    const selected = localConditions.status || [];
    return selected.length > 0 && selected.length === allStatusIds.length;
  }, [localConditions.status, allStatusIds]);

  const isAllExcludingClosedSelected = useMemo(() => {
    const selected = localConditions.status || [];
    return selected.length > 0 && selected.length === allStatusIds.filter((id) => id !== 24).length;
  }, [localConditions.status, allStatusIds]);

  const handleSelectChange =
    <T extends keyof InstructionSearchCondition>(key: T) =>
    (value: InstructionSearchCondition[T]) => {
      setLocalConditions({ ...localConditions, [key]: value });
    };

  const handleStatusChange = (e: SelectChangeEvent<number[]>) => {
    const value = e.target.value as number[];
    handleSelectChange("status")(value);
  };

  const handleAllStatusChange = (checked: boolean) => {
    handleSelectChange("status")(checked ? allStatusIds : []);
  };

  const handleSelectAllExcludingClosed = (checked: boolean) => {
    handleSelectChange("status")(checked ? allStatusIds.filter((id) => id !== 24) : []);
  };

  const updateLocalConditions = (
    key: keyof InstructionSearchCondition,
    value: InstructionSearchCondition[keyof InstructionSearchCondition]
  ) => {
    updateObjectState(setLocalConditions, key, value);
  };

  const handleSearchButtonClick = () => {
    if (localConditions.keyword && (localConditions.keyword.length < 2 || localConditions.keyword.length > 20)) {
      alert("통합 검색어는 최소 2자이상 20자이하로 입력해주세요.");
      return;
    }

    onSearch({
      ...search,
      conditions: {
        ...localConditions,
      },
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearchButtonClick();
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid #ddd",
        borderRadius: 2,
        p: 2,
        mb: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Stack spacing={2}>
        <Stack direction='row' spacing={2} alignItems='center'>
          <DatePicker
            label='발생일'
            value={localConditions.occurredDate ? dayjs(localConditions.occurredDate) : null}
            onChange={(date) => {
              updateLocalConditions("occurredDate", date?.format("YYYY-MM-DD"));
            }}
            format='YYYY-MM-DD'
            slotProps={{ textField: { size: "small", sx: { minWidth: 100, maxWidth: 170 } } }}
            maxDate={localConditions.dueDate ? dayjs(localConditions.dueDate) : dayjs("2500-12-31")}
          />
          <DatePicker
            label='기한일'
            value={localConditions.dueDate ? dayjs(localConditions.dueDate) : null}
            onChange={(date) => {
              updateLocalConditions("dueDate", date?.format("YYYY-MM-DD"));
            }}
            format='YYYY-MM-DD'
            slotProps={{ textField: { size: "small", sx: { minWidth: 100, maxWidth: 170 } } }}
            minDate={localConditions.occurredDate ? dayjs(localConditions.occurredDate) : dayjs("1900-01-01")}
            maxDate={dayjs("2500-12-31")}
          />
          <DatePicker
            label='등록일'
            value={localConditions.createdDate ? dayjs(localConditions.createdDate) : null}
            onChange={(date) => {
              updateLocalConditions("createdDate", date?.format("YYYY-MM-DD"));
            }}
            format='YYYY-MM-DD'
            maxDate={dayjs()}
            slotProps={{ textField: { size: "small", sx: { minWidth: 100, maxWidth: 170 } } }}
          />
          <DepartmentSelect
            label='수명부서'
            sx={{ flex: 3, minWidth: 200 }}
            items={selectedOwnerDept}
            placeholder='수명부서를 선택하세요'
            isCompanyWide={true}
            onChange={(newValue) => {
              const newDepts = newValue;
              setSelectedOwnerDept(newDepts);
              setLocalConditions((prev) => ({
                ...prev,
                ownerDepartment: newDepts.length > 0 ? newDepts[0].id : undefined,
              }));
            }}
          />
          <DepartmentSelect
            label='협조부서'
            maxSelected={7}
            sx={{ flex: 7, minWidth: 200 }}
            placeholder='협조부서를 선택하세요'
            items={selectedCoopDepts}
            isCompanyWide={true}
            roleType={DepartmentRoleType.CC}
            onChange={(newValue) => {
              const newDepts = newValue;
              setSelectedCoopDepts(newDepts);
              setLocalConditions((prev) => ({
                ...prev,
                ccDepartments: newDepts.length > 0 ? newDepts.map((dept) => dept.id) : undefined,
              }));
            }}
          />
        </Stack>

        <Stack direction='row' spacing={2} alignItems='center'>
          <FormControl size='small' sx={{ minWidth: 170, maxWidth: 170 }}>
            <InputLabel>지시유형</InputLabel>
            <Select
              value={localConditions.type ?? 0}
              label='지시유형'
              onChange={(e) => updateLocalConditions("type", Number(e.target.value))}
              renderValue={(selected) =>
                codeValues?.INSTRUCTION_TYPE?.find((type) => type.id === Number(selected))?.label || ALL_SELECTED_TEXT
              }
            >
              <MenuItem value='0'>{ALL_SELECTED_TEXT}</MenuItem>
              {codeValues?.INSTRUCTION_TYPE?.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size='small' sx={{ minWidth: 200, flex: 1, maxWidth: 356 }}>
            <InputLabel id='status-label' shrink>
              지시상태
            </InputLabel>
            <Select
              labelId='status-label'
              label='지시상태'
              multiple
              displayEmpty
              value={localConditions.status || []}
              onChange={handleStatusChange}
              renderValue={(selected) =>
                selected.length === 0
                  ? ALL_SELECTED_TEXT
                  : selected
                      .map((id) => codeValues?.INSTRUCTION_STATUS?.find((status) => status.id === id)?.label)
                      .filter(Boolean)
                      .join(", ")
              }
            >
              <MenuItem value='' sx={{ pl: 2 }}>
                <Checkbox
                  size='small'
                  sx={{ m: 0, p: "0 5px 0 0" }}
                  checked={isAllSelected}
                  indeterminate={!isAllSelected}
                  onChange={(e) => handleAllStatusChange(e.target.checked)}
                />
                <ListItemText primary='전체 선택' />
              </MenuItem>
              <MenuItem value='-1' sx={{ pl: 2 }}>
                <Checkbox
                  size='small'
                  sx={{ m: 0, p: "0 5px 0 0" }}
                  checked={isAllExcludingClosedSelected}
                  indeterminate={!isAllExcludingClosedSelected}
                  onChange={(e) => handleSelectAllExcludingClosed(e.target.checked)}
                />
                <ListItemText primary='종결제외 전체선택' />
              </MenuItem>
              {codeValues?.INSTRUCTION_STATUS?.map((option) => (
                <MenuItem key={option.id} value={option.id} sx={{ pl: 2 }}>
                  <Checkbox
                    size='small'
                    sx={{ m: 0, p: "0 5px 0 0" }}
                    checked={localConditions.status?.includes(option.id)}
                  />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            placeholder='통합 검색어를 입력하세요(최소 2자이상 20자이하)'
            size='small'
            value={localConditions.keyword ?? ""}
            onChange={(e) => updateLocalConditions("keyword", e.target.value)}
            onKeyDown={handleKeyPress}
            inputProps={{ maxLength: 20 }}
            sx={{ flex: 2, minWidth: 200 }}
            slotProps={{
              input: {
                endAdornment: (
                  <>
                    {localConditions.keyword && (
                      <InputAdornment position='end'>
                        <IconButton
                          size='small'
                          onClick={() => updateLocalConditions("keyword", "")}
                          edge='end'
                          tabIndex={-1}
                        >
                          <CloseIcon fontSize='small' />
                        </IconButton>
                      </InputAdornment>
                    )}
                    <InputAdornment position='end'>
                      <span style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                        {`${localConditions.keyword?.length ?? 0}/20 자`}
                      </span>
                    </InputAdornment>
                  </>
                ),
              },
            }}
          />
          <Button
            variant='contained'
            onClick={handleSearchButtonClick}
            sx={{ width: 100, height: 40, ml: 1, bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
          >
            검색
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default SearchArea;
